import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { NotesTimeline } from '@/components/common/NotesTimeline'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Project = Tables<'projects'>
type Quote = Tables<'quotes'>
type Order = Tables<'orders'>
type Activity = Tables<'activity_log'>

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [activity, setActivity] = useState<Activity[]>([])
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    async function load() {
      const [projRes, quotesRes, ordersRes, activityRes] = await Promise.all([
        supabase.from('projects').select('*').eq('id', id!).single(),
        supabase.from('quotes').select('*').eq('project_id', id!).order('created_at', { ascending: false }),
        supabase.from('orders').select('*').eq('project_id', id!).order('created_at', { ascending: false }),
        supabase.from('activity_log').select('*').eq('project_id', id!).order('created_at', { ascending: false }).limit(50),
      ])
      setProject(projRes.data)
      setQuotes(quotesRes.data || [])
      setOrders(ordersRes.data || [])
      setActivity(activityRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading project...</p>
  if (!project) return <p>Project not found</p>

  const siteAddr = project.site_address as Record<string, string> | null

  return (
    <div>
      <PageHeader
        title={project.name}
        description={`${project.reference_code} · ${project.segment.replace(/_/g, ' ')}`}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Link to={`/gaviota/quotes/new?project=${project.id}`} className={css.btnPrimary}>New Quote</Link>
          </div>
        }
      />

      <div className={css.tabs}>
        {['overview', 'quotes', 'orders', 'notes', 'activity'].map(tab => (
          <button key={tab} className={`${css.tab} ${activeTab === tab ? css.tabActive : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)} {tab === 'quotes' ? `(${quotes.length})` : tab === 'orders' ? `(${orders.length})` : ''}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className={css.grid2}>
          <div className={css.card}>
            <h3 style={{ marginBottom: 'var(--space-4)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project Info</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Status</span><StatusBadge label={project.status} /></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Segment</span><span style={{ fontSize: 'var(--text-sm)' }}>{project.segment.replace(/_/g, ' ')}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Estimated Value</span><span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{project.estimated_value ? `£${Number(project.estimated_value).toLocaleString()}` : '-'}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Created</span><span style={{ fontSize: 'var(--text-sm)' }}>{new Date(project.created_at).toLocaleDateString()}</span></div>
            </div>
          </div>

          <div className={css.card}>
            <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Customer & Site</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
              {project.customer_name && <div><strong>{project.customer_name}</strong>{project.customer_company && <span style={{ color: 'var(--color-text-muted)' }}> at {project.customer_company}</span>}</div>}
              {project.customer_email && <div>{project.customer_email}</div>}
              {project.customer_phone && <div>{project.customer_phone}</div>}
              {siteAddr && <div style={{ marginTop: 'var(--space-2)', color: 'var(--color-text-secondary)' }}>{[siteAddr.line1, siteAddr.city, siteAddr.postal_code, siteAddr.country].filter(Boolean).join(', ')}</div>}
              {project.site_notes && <div style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>{project.site_notes}</div>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'quotes' && (
        <div>
          {quotes.length === 0 ? (
            <div className={css.card}><p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No quotes yet. <Link to={`/gaviota/quotes/new?project=${project.id}`}>Create one</Link></p></div>
          ) : (
            <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
              <table className={css.table}>
                <thead><tr><th>Reference</th><th>Version</th><th>Status</th><th>Total</th><th>Created</th></tr></thead>
                <tbody>
                  {quotes.map(q => (
                    <tr key={q.id} className={css.tableClickableRow} onClick={() => window.location.href = `/gaviota/quotes/${q.id}`}>
                      <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{q.reference_code}</code></td>
                      <td>v{q.version}</td>
                      <td><StatusBadge label={q.status} /></td>
                      <td style={{ fontWeight: 500 }}>£{Number(q.total).toLocaleString()}</td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(q.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div>
          {orders.length === 0 ? (
            <div className={css.card}><p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No orders yet</p></div>
          ) : (
            <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
              <table className={css.table}>
                <thead><tr><th>Reference</th><th>Status</th><th>Total</th><th>Requested Delivery</th><th>Created</th></tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} className={css.tableClickableRow} onClick={() => window.location.href = `/gaviota/orders/${o.id}`}>
                      <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{o.reference_code}</code></td>
                      <td><StatusBadge label={o.status} /></td>
                      <td style={{ fontWeight: 500 }}>£{Number(o.total).toLocaleString()}</td>
                      <td style={{ fontSize: 'var(--text-sm)' }}>{o.requested_delivery ? new Date(o.requested_delivery).toLocaleDateString() : '-'}</td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <NotesTimeline
          entityType="project"
          entityId={project.id}
          organizationId={project.organization_id}
        />
      )}

      {activeTab === 'activity' && (
        <div className={css.card}>
          {activity.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No activity recorded yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {activity.map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start', paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)', marginTop: 6, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--text-sm)' }}>{a.summary}</p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
